/**
 * BRANDED CHROME (2026-09-23) — the tenant's Branding header and footer, drawn on
 * customer pages when a journey opts in (`brandedHeader` / `brandedFooter`).
 *
 * One source, two reads:
 *   • Studio (designer, preview): `getBranding` — the admin record, so what the
 *     author just saved on the Branding rail shows here straight away.
 *   • Customer pages: `getPublicBrandingTheme` — the public-safe projection. The
 *     legal links, powered-by switch and custom slots on it are declared in
 *     CD-API-REQUEST-2026-09-23-branded-chrome; until a server states them the
 *     footer draws what it has (logo, name, footer text) and says nothing it can't.
 *
 * Custom markup is rendered in a sandboxed iframe with scripts disabled, after a
 * client-side strip of anything executable. The server sanitises on write too; the
 * client does not rely on it.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { getBranding } from "@v0y4ge/api-client";
import { readPublicBrandingTheme, resetPublicBrandingTheme } from "./public-branding-theme";
import { SETTINGS_OPTS } from "../shell/request-opts";
import { onTenantChange, onTenantSwitching } from "../shell/access";

import { useT } from "@v0y4ge/i18n-runtime";
export interface TenantChrome {
  name: string;
  logoUrl: string | null;
  brandColor: string;
  coverColor: string;
  bodyFont: string;
  footerText: string;
  legalName: string;
  privacyUrl: string;
  termsUrl: string;
  poweredBy: boolean;
  headerMode: "kit" | "custom";
  headerHtml: string;
  footerMode: "kit" | "custom";
  footerHtml: string;
  customCss: string;
  /** brand.faviconUrl; null on every tenant today. */
  faviconUrl: string | null;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const hex = (v: unknown, fb: string): string => (typeof v === "string" && /^#[0-9a-f]{3,8}$/i.test(v) ? v : fb);

function toChrome(raw: unknown): TenantChrome | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const b = (o.brand && typeof o.brand === "object" ? o.brand : o) as Record<string, unknown>;
  return {
    name: str(b.name) || str(b.emailSenderName),
    logoUrl: str(b.logoUrl) || null,
    brandColor: hex(b.brandColor ?? b.primary, "#5142C7"),
    coverColor: hex(b.coverColor ?? b.cover, "#211B56"),
    bodyFont: str(b.bodyFont),
    footerText: str(b.publicFooterText) || str(b.footer),
    legalName: str(b.legalName),
    privacyUrl: str(b.privacyUrl),
    termsUrl: str(b.termsUrl),
    poweredBy: b.poweredBy === true,
    headerMode: b.headerMode === "custom" ? "custom" : "kit",
    headerHtml: str(b.headerHtml),
    footerMode: b.footerMode === "custom" ? "custom" : "kit",
    footerHtml: str(b.footerHtml),
    customCss: str(b.customCss),
    faviconUrl: str(b.faviconUrl) || null,
  };
}

/** THE TAB ICON (CC, 29 Sept). Each public page's HTML declares the neutral bundled
 *  icon (/assets/favicon.svg); once the public theme lands, a tenant's own
 *  brand.faviconUrl replaces it. Only https (or same-origin) URLs are used. Never the
 *  sign-in service's icon. */
export function applyTenantFavicon(url: string | null | undefined): void {
  if (typeof document === "undefined" || !url) return;
  let ok = false;
  try { const u = new URL(url, window.location.href); ok = u.protocol === "https:" || u.origin === window.location.origin; } catch { ok = false; }
  if (!ok) return;
  let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
  link.removeAttribute("type");
  link.href = url;
}

/** In-flight reads. The public theme is kept for the life of the document; the
 *  admin record is only coalesced while a read is running, so every new call after
 *  it settles reads again (a Branding save shows on the next designer mount). */
const cache: { admin?: Promise<TenantChrome | null>; public?: Promise<TenantChrome | null> } = {};
/** Settled reads, so a class component can ask synchronously. null = the read failed. */
const settled: { admin?: TenantChrome | null; public?: TenantChrome | null } = {};
const listeners = new Set<() => void>();
/** Bumped on every invalidation: a read started under an older generation never
 *  writes back, so a response for the previous tenant or kit cannot repopulate. */
let generation = 0;
/** Set while a workspace switch is pending: no read may run under the outgoing
 *  session, so callers wait here until the new tenant is confirmed. */
let switchGate: Promise<void> | null = null;
let releaseSwitchGate: (() => void) | null = null;

function clearChrome(): void {
  generation++;
  delete cache.admin; delete cache.public; delete settled.admin; delete settled.public;
  resetPublicBrandingTheme();
}

/** Studio reads the admin record; a customer page reads the public theme. */
export function loadTenantChrome(studio: boolean): Promise<TenantChrome | null> {
  if (switchGate) return switchGate.then(() => loadTenantChrome(studio));
  const key = studio ? "admin" : "public";
  if (!cache[key]) {
    const gen = generation;
    const read: Promise<TenantChrome | null> = (studio
      ? getBranding(SETTINGS_OPTS as never).then((r: unknown) => toChrome((r as { branding?: unknown })?.branding ?? r))
      : readPublicBrandingTheme().then((r: unknown) => { const c = toChrome((r as { theme?: unknown })?.theme); applyTenantFavicon(c?.faviconUrl); return c; })
    ).then((c) => { if (gen === generation) settled[key] = c; return c; })
     .catch((e: unknown) => { console.warn("branded chrome read failed — journeys fall back to the basic header/footer:", e); if (gen === generation) settled[key] = null; return null; })
     .finally(() => { if (studio && cache.admin === read) delete cache.admin; });
    cache[key] = read;
  }
  return cache[key]!;
}

/** "loading" until the read settles; "failed" when it errored or returned nothing
 *  usable. A failed read never draws an empty branded bar: callers fall back to
 *  the basic chrome. */
export function tenantChromeStatus(studio: boolean): "loading" | "ready" | "failed" {
  const v = settled[studio ? "admin" : "public"];
  return v === undefined ? "loading" : v === null ? "failed" : "ready";
}

/** Called after a Branding save so open designers redraw with the new kit. */
export function invalidateTenantChrome(): void {
  clearChrome();
  listeners.forEach((fn) => fn());
}

export function onTenantChromeChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/* A workspace switch drops the old tenant's kit at once and holds new reads until
 * the new session is confirmed (or rolled back); then open chrome reads again. */
onTenantSwitching(() => {
  if (!switchGate) switchGate = new Promise<void>((resolve) => { releaseSwitchGate = resolve; });
  invalidateTenantChrome();
});
onTenantChange(() => {
  if (!switchGate) return;
  const release = releaseSwitchGate;
  switchGate = null; releaseSwitchGate = null;
  clearChrome();
  release?.();
  listeners.forEach((fn) => fn());
});

/** undefined while loading, null when the read failed, the kit when it landed. */
export function useTenantChrome(studio: boolean, enabled = true): TenantChrome | null | undefined {
  const [chrome, setChrome] = useState<TenantChrome | null | undefined>(() => settled[studio ? "admin" : "public"]);
  const [epoch, setEpoch] = useState(0);
  useEffect(() => onTenantChromeChange(() => { if (switchGate) setChrome(undefined); setEpoch((n) => n + 1); }), []);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    loadTenantChrome(studio).then((c) => { if (live) setChrome(c); });
    return () => { live = false; };
  }, [studio, enabled, epoch]);
  return chrome;
}

/* ── Sanitising ─────────────────────────────────────────────────────────── */

const DROP_TAGS = "script,iframe,frame,frameset,object,embed,applet,link,meta,base,form,input,button,textarea,select,noscript,template";

export function sanitizeSlotHtml(html: string): string {
  if (!html.trim() || typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  doc.body.querySelectorAll(DROP_TAGS).forEach((n) => n.remove());
  doc.body.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (name.startsWith("on") || name === "srcdoc" || name === "formaction") el.removeAttribute(attr.name);
      else if ((name === "href" || name === "src" || name === "xlink:href" || name === "action") && /^(javascript|vbscript|data:text\/html)/.test(value.replace(/\s+/g, ""))) el.removeAttribute(attr.name);
    }
    if (el.tagName === "A") { el.setAttribute("target", "_top"); el.setAttribute("rel", "noopener"); }
  });
  return doc.body.innerHTML;
}

export function sanitizeSlotCss(css: string): string {
  return css
    .replace(/@import[^;]*;?/gi, "")
    .replace(/expression\s*\(/gi, "")
    .replace(/url\(\s*(['"]?)\s*(?:https?:)?\/\/[^)]*\)/gi, "none")
    .replace(/<\/?style[^>]*>/gi, "");
}

/** Author markup, sandboxed: no scripts, links open at the top level. */
function HtmlSlot({ html, css, fontStack, label }: { html: string; css: string; fontStack: string; label: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(64);
  const doc = `<!doctype html><html><head><meta charset="utf-8"><base target="_top"><style>html,body{margin:0;padding:0;}body{font-family:${fontStack};}${sanitizeSlotCss(css)}</style></head><body>${sanitizeSlotHtml(html)}</body></html>`;
  const measure = () => {
    const d = ref.current?.contentDocument;
    if (d) setHeight(Math.max(24, Math.min(720, d.documentElement.scrollHeight)));
  };
  return (
    <iframe ref={ref} title={label} srcDoc={doc} onLoad={measure}
      sandbox="allow-same-origin allow-top-navigation-by-user-activation allow-popups allow-popups-to-escape-sandbox"
      style={{ display: "block", width: "100%", height, border: "none", background: "transparent", flex: "0 0 auto" }} />
  );
}

const fontOf = (c: TenantChrome) => `${c.bodyFont ? `'${c.bodyFont}', ` : ""}var(--jrni-font-family-sans, system-ui), sans-serif`;

/** A tenant logo that falls back when its URL does not load (CC, 2026-10-04): a broken or blocked logo URL
 *  used to leave a broken-image box in the header and footer. Keyed on the URL, so a new logo is tried again. */
function TenantLogo({ src, alt, style, fallback }: { src: string; alt: string; style: CSSProperties; fallback: ReactNode }) {
  const [broken, setBroken] = useState<string | null>(null);
  if (broken === src) return <>{fallback}</>;
  return <img src={src} alt={alt} onError={() => setBroken(src)} style={style} />;
}

export function BrandedHeader({ chrome, tenantName, trailing, fallback }: { chrome: TenantChrome | null | undefined; tenantName?: string | null; trailing?: string; fallback?: ReactNode }) {
  const t = useT();
  if (chrome === null) return <>{fallback ?? null}</>;
  if (!chrome) return <div data-testid="branded-header" aria-busy="true" style={{ height: 52, borderBottom: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-base-white)", flex: "0 0 auto" }} />;
  if (chrome.headerMode === "custom" && chrome.headerHtml.trim()) {
    return <div data-testid="branded-header" data-mode="custom"><HtmlSlot html={chrome.headerHtml} css={chrome.customCss} fontStack={fontOf(chrome)} label={t("lib.brandedChrome.label", "Header")} /></div>;
  }
  const name = chrome.name || tenantName || "";
  return (
    <div data-testid="branded-header" data-mode="kit" style={{ display: "flex", alignItems: "center", gap: 12, rowGap: 8, flexWrap: "wrap", padding: "14px 20px", background: "var(--jrni-color-base-white)", borderBottom: "1px solid var(--jrni-color-surface-border)", fontFamily: fontOf(chrome), flex: "0 0 auto" }}>
      {chrome.logoUrl
        /* A logo that does not load is hidden, not replaced (CC, 2026-10-04): the public theme carries no
           reliable display name to print in its place. */
        ? <TenantLogo src={chrome.logoUrl} alt={name} style={{ height: 28, maxWidth: 180, objectFit: "contain" }} fallback={null} />
        : <span style={{ fontSize: 16, fontWeight: 700, color: chrome.brandColor, letterSpacing: "-0.01em" }}>{name}</span>}
      <div style={{ flex: "1 1 auto" }} />
      {trailing ? <span style={{ fontSize: 12, color: "var(--jrni-color-text-soft)" }}>{trailing}</span> : null}
    </div>
  );
}

/** Self-loading mounts for class components and hosts that hold no chrome state. */
export function BrandedHeaderMount({ studio, tenantName, trailing, fallback }: { studio: boolean; tenantName?: string | null; trailing?: string; fallback?: ReactNode }) {
  return <BrandedHeader chrome={useTenantChrome(studio)} tenantName={tenantName} trailing={trailing} fallback={fallback} />;
}
export function BrandedFooterMount({ studio, tenantName, fallback }: { studio: boolean; tenantName?: string | null; fallback?: ReactNode }) {
  return <BrandedFooter chrome={useTenantChrome(studio)} tenantName={tenantName} fallback={fallback} />;
}

export function BrandedFooter({ chrome, tenantName, fallback }: { chrome: TenantChrome | null | undefined; tenantName?: string | null; fallback?: ReactNode }) {
  const t = useT();
  if (chrome === null) return <>{fallback ?? null}</>;
  if (!chrome) return null;
  if (chrome.footerMode === "custom" && chrome.footerHtml.trim()) {
    return <div data-testid="branded-footer" data-mode="custom"><HtmlSlot html={chrome.footerHtml} css={chrome.customCss} fontStack={fontOf(chrome)} label={t("lib.brandedChrome.label2", "Footer")} /></div>;
  }
  const name = chrome.legalName || chrome.name || tenantName || "";
  const link: CSSProperties = { color: "#FFFFFF", textDecoration: "underline", textUnderlineOffset: 2 };
  return (
    <div data-testid="branded-footer" data-mode="kit" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "18px 24px 22px", background: chrome.coverColor, color: "#FFFFFF", fontFamily: fontOf(chrome), fontSize: 12, textAlign: "center", flex: "0 0 auto" }}>
      {/* No invert filter (CC, 2026-10-04): it flattened a multi-colour logo into a white shape. The logo sits on a
          white chip so it reads on any cover colour; a logo that does not load is simply left out. */}
      {chrome.logoUrl ? (
        <TenantLogo src={chrome.logoUrl} alt={name} fallback={null}
          style={{ height: 22, maxWidth: 160, objectFit: "contain", background: "#FFFFFF", padding: "4px 8px", borderRadius: 6, boxSizing: "content-box" }} />
      ) : null}
      {chrome.footerText ? <span style={{ lineHeight: 1.5, maxWidth: 640 }}>{chrome.footerText}</span> : null}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 16px" }}>
        {name ? <span>© {new Date().getFullYear()} {name}</span> : null}
        {chrome.privacyUrl ? <a href={chrome.privacyUrl} target="_top" rel="noopener" style={link}>{t("lib.brandedChrome.text", "Privacy")}</a> : null}
        {chrome.termsUrl ? <a href={chrome.termsUrl} target="_top" rel="noopener" style={link}>{t("lib.brandedChrome.text2", "Terms")}</a> : null}
        {chrome.poweredBy ? <span>{t("lib.brandedChrome.text3", "Powered by JRNI")}</span> : null}
      </div>
    </div>
  );
}
